import {
	ChangeDetectionStrategy,
	Component,
	inject,
	OnInit,
	signal,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { AlertService } from '@marmon/frontend/alert';
import { DialogBase, DialogOptions } from '@marmon/frontend/common';
import { LoggerService } from '@marmon/frontend/logging';
import { EntitySettingService } from '@marmon/frontend/entity-setting';
import { isEntitySystemMuted } from '@marmon/shared/types/user';
import { TuiButton, TuiLabel, TuiLoader, TuiTitle } from '@taiga-ui/core';
import { TuiButtonLoading, TuiSwitch } from '@taiga-ui/kit';

export type AnalysisSettingsDialogData = {
	analysisId: string;
};

@Component({
	selector: 'mar-analysis-settings-dialog',
	standalone: true,
	imports: [
		ReactiveFormsModule,
		TranslocoPipe,
		TuiButton,
		TuiButtonLoading,
		TuiLabel,
		TuiLoader,
		TuiSwitch,
		TuiTitle,
	],
	templateUrl: 'analysis-settings-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnalysisSettingsDialogComponent
	extends DialogBase<void, AnalysisSettingsDialogData>
	implements OnInit
{
	static override DialogOptions = {
		size: 'm',
		header: 'analyses.dialog.settings.header',
		dismissible: true,
	} as DialogOptions;

	private readonly settings = inject(EntitySettingService);
	private readonly alert = inject(AlertService);
	private readonly logger = inject(LoggerService);

	readonly loading = signal(true);
	readonly saving = signal(false);
	readonly form = new FormGroup({
		systemNotifications: new FormControl(true, { nonNullable: true }),
	});

	ngOnInit() {
		void this.load();
	}

	async load() {
		this.loading.set(true);
		try {
			const setting = await this.settings.get(
				'analysis',
				this.context.data.analysisId,
			);
			this.form.controls.systemNotifications.setValue(
				!isEntitySystemMuted(setting),
			);
		} catch (error) {
			this.logger.captureException(error as Error, {
				source: 'AnalysisSettingsDialog.load',
			});
			this.alert.open('generic-error').subscribe();
			this.onDismiss();
		} finally {
			this.loading.set(false);
		}
	}

	async onSave() {
		if (this.loading() || this.saving()) return;
		this.saving.set(true);
		try {
			await this.settings.setSystemMuted(
				'analysis',
				this.context.data.analysisId,
				!this.form.controls.systemNotifications.value,
			);
			this.alert.open('generic-success').subscribe();
		} catch (error) {
			this.logger.captureException(error as Error, {
				source: 'AnalysisSettingsDialog.save',
			});
			this.alert.open('generic-error').subscribe();
		} finally {
			this.saving.set(false);
		}
	}
}
